import { UiProvider, type UiLabels, type UiLinkComponent } from '@nexus/ui';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Link, RouterProvider } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { ApiError, onSessionEnded } from './api/client';
import { browserLocale, createI18n } from './i18n';
import { ModulesContext } from './modules/context';
import type { WebModule } from './modules/define';
import { checkForAppUpdate, watchAppUpdates } from './offline/app-update';
import { createAppRouter } from './router';
import { onModulesChanged, sessionQuery } from './session/session';
import { recordServerContact } from './sync/sync-status';
import { storedAppearance } from './theme/appearance';

/** Paths go through the router, with their query and anchor; a bare `#anchor` stays in the page. */
const RouterLink: UiLinkComponent = ({
  href,
  children,
  ...props
}: ComponentProps<'a'> & { href: string }) => {
  if (href.startsWith('#')) {
    return (
      <a href={href} {...props}>
        {children}
      </a>
    );
  }
  if (!/[?#]/.test(href)) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    );
  }
  const url = new URL(href, window.location.origin);
  return (
    <Link
      to={url.pathname}
      search={Object.fromEntries(url.searchParams)}
      hash={url.hash.slice(1) || undefined}
      {...props}
    >
      {children}
    </Link>
  );
};

function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onSuccess: recordServerContact }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // Client errors (403, 404, 422) do not get better by retrying.
        retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
      },
    },
  });
}

function TranslatedUi({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const labels = useMemo<UiLabels>(
    () => ({
      close: t('ui.close'),
      loading: t('ui.loading'),
      moreActions: t('ui.moreActions'),
      noResults: t('ui.noResults'),
      required: t('ui.required'),
      optional: t('ui.optional'),
      clear: t('ui.clear'),
      selectAll: t('ui.selectAll'),
      selectRow: t('ui.selectRow'),
      sortAscending: t('ui.sortAscending'),
      sortDescending: t('ui.sortDescending'),
      previousPage: t('ui.previousPage'),
      nextPage: t('ui.nextPage'),
      retry: t('ui.retry'),
      confirm: t('ui.confirm'),
      cancel: t('ui.cancel'),
      breadcrumbs: t('ui.breadcrumbs'),
      filters: t('ui.filters'),
      resetFilters: t('ui.resetFilters'),
      steps: t('ui.steps'),
      stepCompleted: t('ui.stepCompleted'),
      back: t('ui.back'),
      next: t('ui.next'),
    }),
    // The labels change with the language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language],
  );
  return (
    <UiProvider labels={labels} link={RouterLink}>
      {children}
    </UiProvider>
  );
}

export interface NexusAppProps {
  /** Web parts of all modules the build contains. Which are active decides each company. */
  modules: readonly WebModule[];
}

/** The STAW App: kernel shell, login, profile and the pages of all modules. */
export function NexusApp({ modules }: NexusAppProps) {
  const [queryClient] = useState(createQueryClient);
  const [i18n] = useState(() => createI18n(modules, storedAppearance().locale ?? browserLocale()));
  const [router] = useState(() => createAppRouter({ queryClient, modules }));

  // Logout in another tab, expiry or revocation: back to the login page.
  useEffect(
    () =>
      onSessionEnded(() => {
        const { pathname, href } = router.state.location;
        queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'login' });
        if (pathname !== '/login') {
          void router.navigate({ to: '/login', search: { returnTo: href, expired: true } });
        }
      }),
    [router, queryClient],
  );

  // A module was switched on or off: everything loaded for the old set of modules (settings,
  // lists, search, tiles) loads again, and the open page checks again whether it may show. A
  // module this build does not know yet may come with a newer build.
  useEffect(
    () =>
      onModulesChanged(queryClient, () => {
        void queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] !== sessionQuery.queryKey[0],
        });
        void router.invalidate();
        checkForAppUpdate();
      }),
    [router, queryClient],
  );
  useEffect(() => watchAppUpdates(router), [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <TranslatedUi>
          <ModulesContext.Provider value={modules}>
            <RouterProvider router={router} />
          </ModulesContext.Provider>
        </TranslatedUi>
      </I18nextProvider>
    </QueryClientProvider>
  );
}
